import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../themeSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";

const POSE_W = 200;
const POSE_H = 200;
const MIN_YOGA_POSES = 1;
const MAX_YOGA_POSES = 16;
const POSE_NAME_MIN_LEN = 2;
const POSE_NAME_MAX_LEN = 20;
const ALLOWED_POSE_EXT = ["jpeg", "jpg", "png", "gif"];

function normalizePoses(raw) {
  if (!Array.isArray(raw)) return [];
  const used = new Set();
  return raw.map((p, i) => {
    let id = Number(p?.id);
    if (!Number.isFinite(id) || id <= 0 || used.has(id)) {
      id = i + 1;
      while (used.has(id)) id += 1;
    }
    used.add(id);
    return {
      id,
      name: String(p?.name || "").trim(),
      image: String(p?.image || "").trim(),
    };
  });
}

function nextPoseId(poses) {
  if (!poses.length) return 1;
  return Math.max(...poses.map((p) => Number(p.id) || 0)) + 1;
}

function PoseImage({ src, alt, className }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <div className={`catmgr-item-no-img ${className || ""}`}>
        <i className="fa-solid fa-image" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={`catmgr-item-img ${className || ""}`}
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

export default function YogaPosesEditor({
  data,
  currentTheme,
  adminToken,
  gameName,
  onBack,
}) {
  const dispatch = useDispatch();
  const [yogaPoses, setYogaPoses] = useState([]);
  const [saving, setSaving] = useState(false);
  const [poseModal, setPoseModal] = useState(null);
  const [poseName, setPoseName] = useState("");
  const [poseFile, setPoseFile] = useState(null);
  const [posePreview, setPosePreview] = useState("");
  const [poseFilename, setPoseFilename] = useState("");
  const [poseUploading, setPoseUploading] = useState(false);
  const poseFileRef = useRef(null);

  const S3 = process.env.REACT_APP_S3_PATH || "";
  const resolveAssetUrl = (path) => {
    const raw = String(path || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    const rel = raw.replace(/^\/+/, "");
    return `${S3}${encodeURIComponent(rel).replace(/%2F/g, "/")}`;
  };

  useEffect(() => {
    if (!data) return;
    if ("yogaPoses" in data) {
      setYogaPoses(normalizePoses(data.yogaPoses));
    }
  }, [data]);

  const persistPoses = async (nextPoses, successMessage = "Yoga poses updated.") => {
    if (!currentTheme) {
      Swal.fire("No theme", "Select a theme first.", "error");
      return false;
    }
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: { yogaPoses: nextPoses },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setYogaPoses(nextPoses);
      Swal.fire("Saved", successMessage, "success");
      return true;
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed.", "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const uploadPoseImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("themeName", String(currentTheme));
    const res = await fetch(apiUrl("/uploadCategoryImage"), {
      method: "POST",
      headers: { Authorization: `Bearer ${adminToken}` },
      body: fd,
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Upload failed");
    return json.filename;
  };

  const handlePoseFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_POSE_EXT.includes(ext)) {
      Swal.fire("Invalid File", "Only PNG, JPG, JPEG, and GIF are allowed.", "error");
      e.target.value = "";
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (img.width !== POSE_W || img.height !== POSE_H) {
        URL.revokeObjectURL(url);
        Swal.fire(
          "Wrong Dimensions",
          `Image must be exactly ${POSE_W} × ${POSE_H} px. Uploaded image is ${img.width} × ${img.height} px.`,
          "error"
        );
        if (poseFileRef.current) poseFileRef.current.value = "";
        return;
      }
      setPoseFile(file);
      setPosePreview(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      Swal.fire("Invalid Image", "Could not read image dimensions. Please try another file.", "error");
      if (poseFileRef.current) poseFileRef.current.value = "";
    };
    img.src = url;
  };

  const closePoseModal = () => {
    if (posePreview) URL.revokeObjectURL(posePreview);
    setPoseModal(null);
    setPoseName("");
    setPoseFile(null);
    setPosePreview("");
    setPoseFilename("");
    if (poseFileRef.current) poseFileRef.current.value = "";
  };

  const showMaxPosesReached = () => {
    Swal.fire({
      icon: "warning",
      title: "Limit reached",
      text: `You have reached max ${MAX_YOGA_POSES}.`,
      confirmButtonText: "OK",
    });
  };

  const openAddPoseModal = () => {
    if (yogaPoses.length >= MAX_YOGA_POSES) {
      showMaxPosesReached();
      return;
    }
    setPoseModal({ mode: "add" });
    setPoseName("");
    setPoseFile(null);
    setPosePreview("");
    setPoseFilename("");
    if (poseFileRef.current) poseFileRef.current.value = "";
  };

  const openEditPoseModal = (index) => {
    const pose = yogaPoses[index];
    setPoseModal({ mode: "edit", index });
    setPoseName(String(pose?.name || "").slice(0, POSE_NAME_MAX_LEN));
    setPoseFile(null);
    setPosePreview("");
    setPoseFilename(pose?.image || "");
    if (poseFileRef.current) poseFileRef.current.value = "";
  };

  const handlePoseNameChange = (val) => {
    let next = val;
    if (next.length > POSE_NAME_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Pose name can be at most ${POSE_NAME_MAX_LEN} characters.`,
        "warning"
      );
      next = next.slice(0, POSE_NAME_MAX_LEN);
    }
    setPoseName(next);
  };

  const handleSavePose = async () => {
    if (!poseModal) return;
    if (poseModal.mode === "add" && yogaPoses.length >= MAX_YOGA_POSES) {
      showMaxPosesReached();
      closePoseModal();
      return;
    }
    const trimmedName = String(poseName || "").trim();
    if (trimmedName.length < POSE_NAME_MIN_LEN) {
      Swal.fire(
        "Invalid name",
        `Pose name must be ${POSE_NAME_MIN_LEN}-${POSE_NAME_MAX_LEN} characters.`,
        "warning"
      );
      return;
    }

    const duplicate = yogaPoses.findIndex(
      (p, idx) =>
        idx !== poseModal.index &&
        String(p.name).trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (duplicate >= 0) {
      Swal.fire("Duplicate", "A pose with this name already exists.", "warning");
      return;
    }

    let imageFilename = poseFilename;
    if (poseFile) {
      setPoseUploading(true);
      try {
        imageFilename = await uploadPoseImage(poseFile);
      } catch (e) {
        Swal.fire("Upload Error", e.message || "Image upload failed.", "error");
        setPoseUploading(false);
        return;
      }
      setPoseUploading(false);
    }

    if (poseModal.mode === "add" && !imageFilename) {
      Swal.fire("Image required", "Please upload a pose image.", "warning");
      return;
    }
    if (poseModal.mode === "edit" && !imageFilename) {
      Swal.fire("Image required", "Each pose must have an image.", "warning");
      return;
    }

    const duplicateImage = yogaPoses.findIndex(
      (p, idx) =>
        idx !== poseModal.index &&
        String(p.image).trim().toLowerCase() === String(imageFilename).trim().toLowerCase()
    );
    if (duplicateImage >= 0) {
      Swal.fire("Duplicate image", "Another pose already uses this image.", "warning");
      return;
    }

    let nextPoses;
    if (poseModal.mode === "add") {
      nextPoses = [
        ...yogaPoses,
        { id: nextPoseId(yogaPoses), name: trimmedName, image: imageFilename },
      ];
    } else {
      nextPoses = yogaPoses.map((p, idx) =>
        idx === poseModal.index ? { ...p, name: trimmedName, image: imageFilename } : p
      );
    }

    const ok = await persistPoses(
      nextPoses,
      poseModal.mode === "add" ? "Pose added." : "Pose updated."
    );
    if (ok) closePoseModal();
  };

  const handleDeletePose = (index) => {
    if (yogaPoses.length <= MIN_YOGA_POSES) {
      Swal.fire(
        "Cannot delete",
        `At least ${MIN_YOGA_POSES} yoga pose is required.`,
        "warning"
      );
      return;
    }
    const pose = yogaPoses[index];
    Swal.fire({
      title: "Delete pose?",
      text: `"${pose?.name}" will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      const nextPoses = yogaPoses.filter((_, i) => i !== index);
      await persistPoses(nextPoses, "Pose deleted.");
    });
  };

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={onBack}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="catmgr-page">
        <div className="catmgr-header">
          <div className="catmgr-header-left">
            <h2 className="catmgr-title">
              <i className="fa-solid fa-person-praying" /> &nbsp;{gameName} — Yoga Poses
            </h2>
            <p className="catmgr-subtitle">
              {yogaPoses.length} / {MAX_YOGA_POSES} poses &nbsp;·&nbsp; Images must be{" "}
              {POSE_W} × {POSE_H} px (PNG, JPG, JPEG, or GIF)
            </p>
          </div>
          <div className="catmgr-header-right">
            <button
              type="button"
              className="catmgr-btn catmgr-btn-outline"
              onClick={openAddPoseModal}
              disabled={saving}
            >
              <i className="fa-solid fa-plus" /> Add Pose
            </button>
          </div>
        </div>

        <div className="catmgr-body catmgr-body--single">
          <div className="catmgr-right-panel catmgr-right-panel--full">
            {yogaPoses.length === 0 ? (
              <div className="catmgr-empty">
                No yoga poses yet. Click <strong>Add Pose</strong> to create one (minimum{" "}
                {MIN_YOGA_POSES} required).
              </div>
            ) : (
              <div className="catmgr-card">
                <div className="catmgr-items-grid catmgr-items-grid--poses">
                  {yogaPoses.map((pose, index) => (
                    <div className="catmgr-item-card catmgr-item-card--pose" key={`${pose.id}-${index}`}>
                      <div className="catmgr-item-img-wrap catmgr-item-img-wrap--pose">
                        <PoseImage
                          src={resolveAssetUrl(pose.image)}
                          alt={pose.name}
                        />
                      </div>
                      <div className="catmgr-item-info">
                        <span className="catmgr-item-name" title={pose.name}>
                          {pose.name}
                        </span>
                        <span className="catmgr-cat-key">ID: {pose.id}</span>
                      </div>
                      <div className="catmgr-item-btns">
                        <button
                          type="button"
                          className="catmgr-icon-btn catmgr-icon-btn-sm"
                          title="Edit"
                          onClick={() => openEditPoseModal(index)}
                          disabled={saving}
                        >
                          <i className="fa-solid fa-pen" />
                        </button>
                        <button
                          type="button"
                          className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                          title="Delete"
                          onClick={() => handleDeletePose(index)}
                          disabled={saving}
                        >
                          <i className="fa-solid fa-trash" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {poseModal && (
        <>
          <div className="catmgr-backdrop" onClick={closePoseModal} />
          <div className="catmgr-modal catmgr-modal-wide">
            <h3>{poseModal.mode === "add" ? "Add Yoga Pose" : "Edit Yoga Pose"}</h3>

            <label className="catmgr-modal-label">Pose name</label>
            <input
              autoFocus
              className="catmgr-modal-input"
              placeholder="e.g. Tree Pose"
              value={poseName}
              onChange={(e) => handlePoseNameChange(e.target.value)}
              maxLength={POSE_NAME_MAX_LEN}
            />
            <p className="catmgr-modal-hint" style={{ marginTop: 4 }}>
              * {POSE_NAME_MIN_LEN}-{POSE_NAME_MAX_LEN} characters.
            </p>

            <label className="catmgr-modal-label" style={{ marginTop: 16 }}>
              Image &nbsp;
              <span className="catmgr-optional">
                (PNG / JPG / JPEG / GIF — exactly {POSE_W} × {POSE_H} px)
              </span>
            </label>

            <div className="catmgr-upload-preview">
              {posePreview ? (
                <img src={posePreview} alt="preview" className="catmgr-upload-thumb" />
              ) : poseFilename ? (
                <PoseImage
                  src={resolveAssetUrl(poseFilename)}
                  alt="current"
                  className="catmgr-upload-thumb"
                />
              ) : (
                <div className="catmgr-upload-placeholder">
                  <i className="fa-solid fa-image" />
                  <span>
                    {POSE_W} × {POSE_H} px
                  </span>
                </div>
              )}
            </div>

            <input
              ref={poseFileRef}
              type="file"
              accept=".png,.jpg,.jpeg,.gif"
              className="catmgr-file-input"
              onChange={handlePoseFileChange}
            />
            <p className="catmgr-modal-hint">
              * {poseModal.mode === "add" ? "Image required." : "Leave empty to keep current image."}{" "}
              Exactly {POSE_W} × {POSE_H} px. Max 5 MB.
            </p>

            <div className="catmgr-modal-actions">
              <button type="button" onClick={closePoseModal}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                onClick={handleSavePose}
                disabled={poseUploading || saving}
              >
                {poseUploading ? "Uploading…" : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
